/**
 * Sentry init for the LaneBooker admin SPA.
 *
 * Each page entry (admin.ts, login.ts, verify.ts, platform-page.ts,
 * incidents-page.ts) imports `initSentry()` at the very top so unhandled
 * errors + promise rejections in any module load are captured.
 *
 * DSN is injected at build time via `VITE_SENTRY_DSN`. When unset (dev or
 * a preview build without the secret), `initSentry` is a no-op.
 *
 * Tenant slug is tagged when the page knows it — call `tagAdminTenant(slug)`
 * once the slug is resolved (typically in admin.ts after `resolveTenantSlug`).
 */

import * as Sentry from '@sentry/browser';
import { resolveTenantSlug } from './tenant';

/**
 * Default Sentry DSN for the admin SPA.
 *
 * DSNs are PUBLIC identifiers — they ship in browser JS on every page load
 * and are designed to be exposed. Safe to commit. To rotate, generate a new
 * DSN in the Sentry dashboard and update this constant. `VITE_SENTRY_DSN`
 * still wins when set, so preview/PR builds can override at build time.
 */
const DEFAULT_DSN_PRODUCTION = 'https://51250d7d9715869cc4b6ddb3d0e4bfd4@o4511451222114304.ingest.us.sentry.io/4511451240202240';

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined)
  || (import.meta.env.MODE === 'production' ? DEFAULT_DSN_PRODUCTION : '');
const ENV_NAME = (import.meta.env.VITE_SENTRY_ENV as string | undefined) || (import.meta.env.MODE === 'production' ? 'production' : 'development');
const RELEASE = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || 'lanebooker-admin@0.6.0';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (!DSN) {
    // Silent in dev so console isn't noisy. Visible in build env via build log.
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENV_NAME,
    release: RELEASE,
    // Errors + unhandled promise rejections are on by default in Browser SDK.
    // No tracing/replay — we only want error reporting for now.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      // Drop noisy / known-benign events.
      const msg = event.message ?? event.exception?.values?.[0]?.value ?? '';
      if (/ResizeObserver loop|Non-Error promise rejection captured/i.test(msg)) {
        return null;
      }
      return event;
    },
  });

  // Best-effort tenant tag at boot — pages can call `tagAdminTenant` later
  // once the active slug is locked in.
  try {
    const resolved = resolveTenantSlug();
    if (resolved?.slug) {
      Sentry.setTag('tenant_slug', resolved.slug);
    }
  } catch {
    // tenant.ts is safe but resolveTenantSlug touches window.location — ignore.
  }
  Sentry.setTag('app', 'admin');
  initialized = true;
}

/** Update the tenant tag after the admin SPA has loaded one. */
export function tagAdminTenant(slug: string | null): void {
  if (!initialized) return;
  if (slug) Sentry.setTag('tenant_slug', slug);
}

/** Tag the authenticated user (after `ensureAuth`). PII-light: id + email only. */
export function tagAdminUser(user: { id: string; email: string } | null): void {
  if (!initialized) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id, email: user.email });
}

/** Manual capture for places where we catch + handle but still want a signal. */
export function captureAdminError(err: unknown, ctx?: Record<string, unknown>): void {
  if (!initialized) return;
  if (ctx) Sentry.setContext('admin', ctx);
  Sentry.captureException(err);
}
